import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

/* ─── Types ─────────────────────────────────────────────────── */
export type ProgressVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger';
export type ProgressSize = 'sm' | 'md' | 'lg';

const variantColors: Record<ProgressVariant, string> = {
  default: 'bg-gray-500 dark:bg-gray-400',
  primary: 'bg-primary-900 dark:bg-primary-200',
  success: 'bg-green-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
};

const trackSizes: Record<ProgressSize, string> = {
  sm: 'h-1',
  md: 'h-2.5',
  lg: 'h-4',
};

export interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: number;
  max?: number;
  variant?: ProgressVariant;
  size?: ProgressSize;
  showLabel?: boolean;
  indeterminate?: boolean;
}

export const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      className,
      value = 0,
      max = 100,
      variant = 'primary',
      size = 'md',
      showLabel = false,
      indeterminate = false,
      ...props
    },
    ref
  ) => {
    const pct = Math.min(100, Math.max(0, (value / max) * 100));

    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        {showLabel && !indeterminate && (
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
              Progress
            </span>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {Math.round(pct)}%
            </span>
          </div>
        )}

        <div
          role="progressbar"
          aria-valuenow={indeterminate ? undefined : value}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-label={indeterminate ? 'Loading…' : undefined}
          aria-valuetext={indeterminate ? undefined : `${Math.round(pct)}%`}
          className={cn(
            'w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700',
            trackSizes[size]
          )}
        >
          {indeterminate ? (
            <motion.div
              className={cn('h-full w-1/3 rounded-full', variantColors[variant])}
              animate={{ x: ['-100%', '400%'] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
            />
          ) : (
            <motion.div
              className={cn('h-full rounded-full', variantColors[variant])}
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          )}
        </div>
      </div>
    );
  }
);

Progress.displayName = 'Progress';
